import { type Activity, catalog, type Crime, type Recipe } from '@wyn/content';
import { balance, DAY } from './config/balance.ts';

export type SpendBar = 'focus' | 'vigour' | 'nerve';

/** A bar's regeneration over a day. */
const perDay = (bar: SpendBar) => {
  const b = balance.bars[bar];
  return (DAY / b.intervalMs) * b.rate;
};

/**
 * What one point of a bar is worth against a point of Focus: the scarcer the bar, the more each point
 * pays. Nerve comes back a tenth as fast, so a point of Nerve pays ten points of Focus. Every bar then
 * earns about the same per hour of regeneration.
 */
export const barWeight = (bar: SpendBar) => perDay('focus') / perDay(bar);

/** Base XP per point of Focus at an activity of `level` (before the score multiplier, 0.6 + score). */
export const xpPerPoint = (level: number) => balance.rewards.xpPerPoint.base + balance.rewards.xpPerPoint.perLevel * level;

/** The multiplier a typical player's score earns: settlement pays `xp × (0.6 + score)`. */
const typical = () => 0.6 + balance.targets.typicalScore;

/** The highest level any venue activity needs; rewards stop growing there. */
export const MAX_ACTIVITY_LEVEL = Math.max(...catalog.minigames.flatMap((g) => g.activities.map((a) => a.level)));

/** Base XP for a venue activity: its cost, weighted by bar, times the XP a point earns at its level. */
export const activityXp = (a: Pick<Activity, 'cost' | 'level'>) => Math.round(a.cost.amount * barWeight(a.cost.bar as SpendBar) * xpPerPoint(a.level));

/**
 * XP a recipe gives each skill it names. Together they come to what a typical attempt at a venue of the
 * recipe's highest level would pay for the same bar.
 */
export const recipeXp = (r: Pick<Recipe, 'cost' | 'skills'>) =>
  Math.round((r.cost.amount * barWeight(r.cost.bar as SpendBar) * xpPerPoint(Math.max(...r.skills.map((s) => s.level))) * typical()) / r.skills.length);

/** Renown a typical player earns per point of a bar at a venue of `level`. */
export const typicalRenownPerPoint = (bar: SpendBar, level: number) => barWeight(bar) * xpPerPoint(level) * typical() * balance.minigames.renownPerXp;

/**
 * Renown for pulling off a petty crime: what a typical player would earn for the same Nerve at a
 * first-tier venue, divided by the chance of success, so the expectation matches.
 */
export const crimeRenown = (c: Pick<Crime, 'nerve' | 'successChance'>) => Math.round((c.nerve * typicalRenownPerPoint('nerve', 1)) / c.successChance);

/**
 * Renown for training: what the Vigour would earn a typical player at a first-tier venue. Training pays
 * in stats rather than skill and goods, so it earns Renown at the base rate, and venues pay more as their
 * tiers open.
 */
export const trainingRenown = (vigour: number) => Math.round(vigour * typicalRenownPerPoint('vigour', 1));

/**
 * Renown for winning a fight against a player of `loserLevel`, before Spare. A win is worth
 * `fight.renown.winMultiple` times what the Vigour would earn at a venue of that level (capped at the
 * highest venue level), since an even fight is won about half the time.
 */
export const fightRenown = (loserLevel: number) =>
  Math.round(balance.fight.vigour * typicalRenownPerPoint('vigour', Math.min(loserLevel, MAX_ACTIVITY_LEVEL)) * balance.fight.renown.winMultiple);
